import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { isEmpty, isNil, noop } from 'lodash';
// Import UI components
import { BusyLoader } from '@geneui/components';
// Import Components
import SegmentGeneralInfo from './SegmentGeneralInfo';
import SegmentActionsButtons from 'components/SegmentList/SegmentActionsButtons';
// Import Services
import { SegmentHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useQueryParams from 'hooks/UseQueryParams';
import useGoToRoute from 'hooks/GoToRoute';
//Import Constants
import { l, SegmentTypesLabels } from 'constants/common';
import { modalsStateSetDictKeys } from 'pages/CampaignListPage/config';
import { RoutesList } from 'routes';

const {
    SEGMENT_DYNAMIC_CLONE,
    SEGMENT_COMPOSITE_CLONE,
    CONVERT_TO_REPORT,
    SEGMENT_DYNAMIC_EDIT,
    SEGMENT_COMPOSITE_EDIT,
} = RoutesList;

const SegmentInfoModalBody = ({
    isLoading,
    segmentInfo,
    segmentInfoType,
    isPredefined,
    usedInCampaigns,
    renderSegmentInfo,
    handleInfoModalActionClick,
    withActionButtons,
    predefinedKeyValue,
}) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const { goToRoute, getRoutePath } = useGoToRoute();
    const { search } = useLocation();
    const goToRouteQueryParams = useQueryParams();

    const { getCampaignsBySegmentRequest } = useCancelableRequests({
        getCampaignsBySegmentRequest: SegmentHttpService.getCampaignsBySegment,
    });

    // Actions part
    const getConvertSegmentToReportPath = ({ SegmentId }) => {
        const params = { segmentId: SegmentId };
        const path = CONVERT_TO_REPORT;
        return getRoutePath(path, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const deleteSegmentAction = (segment) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.deleteSegment, segment);
    };

    const archiveSegmentAction = (segment) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.archiveSegment, segment);
    };

    const unArchiveSegmentAction = (segment) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.unarchiveSegment, segment);
    };

    const getCloneSegmentPath = ({ Type, SegmentId }) => {
        const params = { segmentId: SegmentId };
        if (SegmentTypesLabels.Dynamic === Type) {
            return getRoutePath(SEGMENT_DYNAMIC_CLONE, params);
        } else {
            return getRoutePath(SEGMENT_COMPOSITE_CLONE, params);
        }
    };

    const getEditSegmentPath = (segment) => {
        const { Type, SegmentId } = segment;
        const params = { segmentId: SegmentId };
        const path = SegmentTypesLabels.Dynamic === Type ? SEGMENT_DYNAMIC_EDIT : SEGMENT_COMPOSITE_EDIT;

        return getRoutePath(path, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const goToEditSegment = (segment) => {
        goToRoute(getEditSegmentPath(segment));
    };

    const editSegmentAction = (segment) => {
        doGetRequest(getCampaignsBySegmentRequest.request, {
            queryString: { segmentId: segment.SegmentId },
            successCallback: (Data) => {
                if (isEmpty(Data)) {
                    goToEditSegment(segment);
                } else {
                    handleInfoModalActionClick(modalsStateSetDictKeys.editSegment, {
                        objects: Data.map(({ Name, Type }) => ({ Name, Type })),
                        segment,
                    });
                }
            },
        });
    };

    return (
        <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="medium">
            {!isNil(segmentInfo) && (
                <>
                    {withActionButtons && (
                        <div className="crm-info-segment-action-buttons-container">
                            <SegmentActionsButtons
                                data={{ ...segmentInfo, Name: { Name: segmentInfo.Name } }}
                                isInfoVisible={false}
                                isViewMode={false}
                                getConvertSegmentToReportPath={getConvertSegmentToReportPath}
                                unArchiveSegmentAction={unArchiveSegmentAction}
                                archiveSegmentAction={archiveSegmentAction}
                                deleteSegmentAction={deleteSegmentAction}
                                getCloneSegmentPath={getCloneSegmentPath}
                                editSegmentAction={editSegmentAction}
                                getEditSegmentPath={getEditSegmentPath}
                            />
                        </div>
                    )}
                    <SegmentGeneralInfo
                        segmentId={segmentInfo.SegmentId}
                        targetObject={segmentInfo.TargetObject}
                        name={segmentInfo.Name}
                        description={segmentInfo.Description}
                        type={segmentInfoType}
                        isPredefined={isPredefined}
                        usedInCampaigns={usedInCampaigns}
                        compositeConfiguration={segmentInfo.CompositeConfiguration}
                        predefinedKeyValue={predefinedKeyValue}
                    />
                    <div className="crm-object-modal-bottom">
                        {/*For title text can add active-state class*/}
                        <div className="title-wrapper">
                            <p className="title-wrapper-text">{t(l.SegmentInfo)}</p>
                            <span className="title-wrapper-line" />
                        </div>
                        <div className="crm-object-modal-b-content campaign-info-modal-segment-container">
                            {renderSegmentInfo()}
                        </div>
                    </div>
                </>
            )}
        </BusyLoader>
    );
};

SegmentInfoModalBody.propTypes = {
    segmentInfo: PropTypes.object,
    segmentInfoType: PropTypes.number.isRequired,
    isPredefined: PropTypes.bool,
    renderSegmentInfo: PropTypes.func,
    usedInCampaigns: PropTypes.array,
    isLoading: PropTypes.bool,
    handleInfoModalActionClick: PropTypes.func,
    withActionButtons: PropTypes.bool,
    predefinedKeyValue: PropTypes.object,
};

SegmentInfoModalBody.defaultProps = {
    isLoading: false,
    isPredefined: false,
    renderSegmentInfo: noop,
    usedInCampaigns: null,
    withActionButtons: false,
};

export default SegmentInfoModalBody;
